import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarCheck2, ChevronLeft, DoorOpen, Info } from 'lucide-react';
import type { CheckInArrival, CheckInFindResult, PublicCheckInPlace } from '@rx-manager/shared';
import { BookingApiError, checkInApi } from '../../lib/api';
import { useI18n, type MessageKey } from '../../lib/i18n';
import { dobProblem } from '../../lib/validate';
import {
  Alert,
  Button,
  CHECKBOX,
  ChoiceCard,
  Field,
  HEADING,
  Input,
  StepActions,
  SuccessMark,
  cn,
} from '../../components/ui';
import {
  ArrivalStatus,
  TextWhenCalledBox,
  checkInFailure,
  clockTime,
  failureText,
  ticketsOf,
  tile,
  wallClock,
  type Failure,
} from './shared';
import { WalkInJoin } from './WalkInJoin';

/**
 * Track CIQ (CIQ-D2 to D5, D7, D8) — checking in at a place (`/i/<code>`): the poster's QR on a
 * patient's phone, and with `?kiosk=1` the tablet at the counter.
 *
 * "I have an appointment" finds today's visits there by last name and birth date, which travel in
 * the request's body and never in an address: first names and times come back, never a service, a
 * party's people with whoever was found. "I'm here" puts them in line and gives their number.
 * "I'm walking in", while the place takes walk-ins, joins the line for a service (WalkInJoin).
 *
 * The kiosk (CIQ-D7) has larger type, keeps no link and no token, never shows a service, and starts
 * over by itself: 20 seconds after a check-in, and after a minute untouched on any screen.
 */

/** The kiosk goes back to its start this long after a check-in… */
const KIOSK_DONE_MS = 20_000;
/** …and this long after the last touch or key, from any screen. */
const KIOSK_IDLE_MS = 60_000;
/** Walk-ins open and close through the day: a kiosk waiting at its start looks again this often. */
const KIOSK_REFRESH_MS = 5 * 60_000;

type Found = Extract<CheckInFindResult, { outcome: 'found' }>;

/** What Find my visit said short of a match, or that a match took too long. */
type FindNotice =
  | { kind: 'not_found' }
  | { kind: 'too_early'; opensAt: string }
  | { kind: 'too_late' }
  | { kind: 'ask_counter' }
  | { kind: 'expired' };

/** An arrival as this page keeps it: on the kiosk, without its link. */
type Arrival = Omit<CheckInArrival, 'queueToken'> & { queueToken: string | null };

type Screen =
  | { kind: 'home' }
  | { kind: 'find'; notice: FindNotice | null }
  | { kind: 'found'; match: Found }
  | { kind: 'walk_in' }
  | { kind: 'done'; arrival: Arrival };

type FindErrors = { lastName?: MessageKey; birthDate?: MessageKey };

function noticeOf(result: Exclude<CheckInFindResult, Found>): FindNotice {
  switch (result.outcome) {
    case 'too_early':
      return { kind: 'too_early', opensAt: result.opensAt };
    case 'too_late':
      return { kind: 'too_late' };
    case 'not_found':
      return { kind: 'not_found' };
    default:
      // Five misses, a record without a birth date, or anything newer: the counter can help.
      return { kind: 'ask_counter' };
  }
}

export function CheckInPage({
  code,
  place,
  kiosk,
  onRefresh,
}: {
  code: string;
  place: PublicCheckInPlace;
  kiosk: boolean;
  /** The place again, quietly; null when it could not be had. */
  onRefresh: () => Promise<PublicCheckInPlace | null>;
}) {
  const { t, locale, setLocale } = useI18n();
  const navigate = useNavigate();
  const [screen, setScreen] = useState<Screen>({ kind: 'home' });
  const [lastName, setLastName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [findErrors, setFindErrors] = useState<FindErrors>({});
  /** The refs ticked on a match: everyone not already checked in, until unticked. */
  const [picked, setPicked] = useState<string[]>([]);
  const [texts, setTexts] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  /** Moved on by Start over, so an answer meant for the last person never reaches the next. */
  const generation = useRef(0);
  /** The language the kiosk starts each person in. */
  const startLocale = useRef(locale);

  const walkInServices = place.walkIns.open ? place.walkIns.services : [];
  const takesWalkIns = walkInServices.length > 0;

  // A new screen says so: its heading takes the focus.
  useEffect(() => {
    headingRef.current?.focus();
  }, [screen.kind]);

  const go = (next: Screen): void => {
    setFailure(null);
    setScreen(next);
  };

  /**
   * Back to the start. On a phone it is the same person, so what they typed stays; the kiosk's
   * start has no timer, so it keeps nothing there for the next person to find.
   */
  const toStart = (): void => {
    if (kiosk) {
      setLastName('');
      setBirthDate('');
      setFindErrors({});
      setPicked([]);
      setTexts(false);
    }
    go({ kind: 'home' });
  };

  /** Back to the start with nothing kept: the kiosk's timers, and "Start over". */
  const startOver = useCallback((): void => {
    generation.current += 1;
    setScreen({ kind: 'home' });
    setLastName('');
    setBirthDate('');
    setFindErrors({});
    setPicked([]);
    setTexts(false);
    setFailure(null);
    setBusy(false);
    if (kiosk) {
      // The next person starts in the place's own language, with walk-ins as they are now.
      setLocale(startLocale.current);
      void onRefresh();
    }
  }, [kiosk, onRefresh, setLocale]);

  // The kiosk: back to the start 20 seconds after a check-in…
  useEffect(() => {
    if (!kiosk || screen.kind !== 'done') return;
    const timer = window.setTimeout(startOver, KIOSK_DONE_MS);
    return () => window.clearTimeout(timer);
  }, [kiosk, screen.kind, startOver]);

  // …after a minute with no touch and no key, from any other screen…
  useEffect(() => {
    if (!kiosk || screen.kind === 'home') return;
    let timer = window.setTimeout(startOver, KIOSK_IDLE_MS);
    const touched = (): void => {
      window.clearTimeout(timer);
      timer = window.setTimeout(startOver, KIOSK_IDLE_MS);
    };
    window.addEventListener('pointerdown', touched, true);
    window.addEventListener('keydown', touched, true);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('pointerdown', touched, true);
      window.removeEventListener('keydown', touched, true);
    };
  }, [kiosk, screen.kind, startOver]);

  // …and, waiting at its start, it looks at the place again now and then.
  useEffect(() => {
    if (!kiosk || screen.kind !== 'home') return;
    const timer = window.setInterval(() => void onRefresh(), KIOSK_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [kiosk, screen.kind, onRefresh]);

  /** Where they stand now. Nothing they typed is kept a moment longer. */
  const arrived = (result: CheckInArrival): void => {
    setLastName('');
    setBirthDate('');
    setFindErrors({});
    setPicked([]);
    setTexts(false);
    setFailure(null);
    setScreen({
      kind: 'done',
      arrival: {
        ticket: result.ticket,
        tickets: result.tickets,
        ahead: result.ahead,
        // CIQ-D7: the kiosk keeps no link to anyone's place in line.
        queueToken: kiosk ? null : result.queueToken,
      },
    });
  };

  const find = async (): Promise<void> => {
    const errors: FindErrors = {};
    if (!lastName.trim()) errors.lastName = 'error.fieldRequired';
    const dob = dobProblem(birthDate);
    if (dob) errors.birthDate = dob;
    setFindErrors(errors);
    if (errors.lastName || errors.birthDate) {
      document.getElementById(errors.lastName ? 'check-in-last' : 'check-in-dob')?.focus();
      return;
    }
    const at = generation.current;
    setBusy(true);
    setFailure(null);
    try {
      const result = await checkInApi.find(code, { lastName: lastName.trim(), birthDate });
      if (at !== generation.current) return;
      if (result.outcome === 'found') {
        setPicked(result.people.filter((person) => !person.checkedIn).map((person) => person.ref));
        setTexts(false);
        setScreen({ kind: 'found', match: result });
      } else {
        setScreen({ kind: 'find', notice: noticeOf(result) });
      }
    } catch (error) {
      if (at === generation.current) setFailure(checkInFailure(error));
    } finally {
      if (at === generation.current) setBusy(false);
    }
  };

  const arrive = async (match: Found): Promise<void> => {
    const at = generation.current;
    setBusy(true);
    setFailure(null);
    try {
      const result = await checkInApi.arrive(code, {
        matchToken: match.matchToken,
        // In the match's order: a text goes to the first person's mobile (CIQ-D4).
        refs: match.people.filter((person) => picked.includes(person.ref)).map((person) => person.ref),
        textWhenCalled: match.text.offered && texts,
        via: kiosk ? 'kiosk' : 'poster',
        language: locale,
      });
      if (at === generation.current) arrived(result);
    } catch (error) {
      if (at !== generation.current) return;
      // The match is good for ten minutes: after that, find the visit again.
      if (
        error instanceof BookingApiError &&
        (error.code === 'match_expired' || error.code === 'unknown_person' || error.status === 410)
      ) {
        setScreen({ kind: 'find', notice: { kind: 'expired' } });
        return;
      }
      setFailure(checkInFailure(error));
    } finally {
      if (at === generation.current) setBusy(false);
    }
  };

  const follow = (token: string): void => {
    // Their place in line opens in the language they checked in in; the token is in its address only.
    navigate(`/q/${encodeURIComponent(token)}`, { state: { lang: locale } });
  };

  const failureBox = failure && <Alert>{failureText(failure, t)}</Alert>;

  if (screen.kind === 'walk_in') {
    return (
      <WalkInJoin
        code={code}
        place={place}
        kiosk={kiosk}
        prefill={{ lastName, birthDate }}
        onBack={toStart}
        onJoined={arrived}
        onStartOver={startOver}
        onReload={onRefresh}
      />
    );
  }

  if (screen.kind === 'done') {
    const { arrival } = screen;
    const token = arrival.queueToken;
    return (
      <div className="space-y-6">
        <div className="flex flex-col items-center gap-4 pt-2 text-center">
          <SuccessMark />
          <h1 ref={headingRef} tabIndex={-1} className={HEADING}>
            {t('checkIn.checkedIn')}
          </h1>
          {/* Never the service (CIQ-D3, D40): the number, and how many are ahead. */}
          <ArrivalStatus tickets={ticketsOf(arrival)} ahead={arrival.ahead} seat={kiosk} />
        </div>
        {token && (
          <Button size="lg" full onClick={() => follow(token)}>
            {t('checkIn.follow')}
          </Button>
        )}
      </div>
    );
  }

  if (screen.kind === 'found') {
    const { match } = screen;
    const left = match.people.filter((person) => !person.checkedIn);
    return (
      <div className="space-y-6">
        <h1 ref={headingRef} tabIndex={-1} className={HEADING}>
          {t('checkIn.foundHeading')}
        </h1>
        {failureBox}
        <fieldset className="space-y-3">
          <legend className="text-base font-semibold text-heading">{t('checkIn.whoIsHere')}</legend>
          <div className="grid gap-2">
            {match.people.map((person) => {
              const chosen = person.checkedIn || picked.includes(person.ref);
              return (
                <label key={person.ref} className={tile(chosen, person.checkedIn)}>
                  <input
                    type="checkbox"
                    checked={chosen}
                    disabled={person.checkedIn}
                    onChange={() =>
                      setPicked((current) =>
                        current.includes(person.ref)
                          ? current.filter((ref) => ref !== person.ref)
                          : [...current, person.ref],
                      )
                    }
                    className={CHECKBOX}
                  />
                  <span className="flex min-w-0 flex-1 flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5">
                    <span className="text-base font-semibold text-heading">{person.firstName}</span>
                    <span className="text-base tabular-nums text-muted-foreground">{wallClock(person.time, locale)}</span>
                    {person.checkedIn && (
                      <span className="w-full text-sm text-muted-foreground">{t('checkIn.alreadyIn')}</span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>
        {left.length === 0 && (
          <p className="rounded-xl bg-muted/70 p-4 text-base text-foreground">{t('checkIn.allIn')}</p>
        )}
        {left.length > 0 && match.text.offered && (
          <TextWhenCalledBox
            checked={texts}
            onChange={setTexts}
            digits={match.text.phoneEnding}
            consent={place.consent}
          />
        )}
        <StepActions>
          <Button variant="outline" onClick={() => go({ kind: 'find', notice: null })} disabled={busy}>
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            {t('step.back')}
          </Button>
          {left.length > 0 && (
            <Button
              onClick={() => void arrive(match)}
              loading={busy}
              disabled={picked.length === 0}
              className="flex-1 sm:flex-none"
            >
              {t('checkIn.imHere')}
            </Button>
          )}
        </StepActions>
      </div>
    );
  }

  if (screen.kind === 'find') {
    return (
      // `noValidate`: our own words in the page's language, not the browser's.
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void find();
        }}
        className="space-y-6"
      >
        <div className="space-y-2">
          <h1 ref={headingRef} tabIndex={-1} className={HEADING}>
            {t('checkIn.find')}
          </h1>
          <p className="text-base text-muted-foreground">{t('checkIn.findLead')}</p>
        </div>
        {failureBox}
        {screen.notice && (
          <FindNoticeBox
            notice={screen.notice}
            timeZone={place.place.timezone}
            takesWalkIns={takesWalkIns}
            onWalkIn={() => go({ kind: 'walk_in' })}
          />
        )}
        <div className="grid gap-5 sm:grid-cols-2">
          <Field
            id="check-in-last"
            label={t('details.lastName')}
            error={findErrors.lastName ? t(findErrors.lastName) : undefined}
            required
          >
            {(props) => (
              <Input
                {...props}
                // A counter's tablet keeps nobody's name for the next person to be offered.
                autoComplete={kiosk ? 'off' : 'family-name'}
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
              />
            )}
          </Field>
          <Field
            id="check-in-dob"
            label={t('details.dob')}
            error={findErrors.birthDate ? t(findErrors.birthDate) : undefined}
            required
          >
            {(props) => (
              <Input
                {...props}
                type="date"
                autoComplete={kiosk ? 'off' : 'bday'}
                value={birthDate}
                onChange={(event) => setBirthDate(event.target.value)}
              />
            )}
          </Field>
        </div>
        <StepActions>
          <Button variant="outline" onClick={toStart} disabled={busy}>
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            {t('step.back')}
          </Button>
          <Button type="submit" loading={busy} className="flex-1 sm:flex-none">
            {t('checkIn.find')}
          </Button>
        </StepActions>
      </form>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h1 ref={headingRef} tabIndex={-1} className={HEADING}>
          {t('checkIn.heading', { place: place.place.name })}
        </h1>
        <p className="text-base text-muted-foreground">{t('checkIn.lead')}</p>
      </div>
      {failureBox}
      <div className="grid gap-3">
        <ChoiceCard
          icon={CalendarCheck2}
          title={t('checkIn.haveAppointment')}
          detail={t('checkIn.haveAppointmentHelp')}
          onClick={() => go({ kind: 'find', notice: null })}
        />
        {takesWalkIns ? (
          <ChoiceCard
            icon={DoorOpen}
            title={t('checkIn.walkingIn')}
            detail={t('checkIn.walkingInHelp')}
            onClick={() => go({ kind: 'walk_in' })}
          />
        ) : (
          <p className="flex items-start gap-3 rounded-xl bg-muted/70 p-4 text-base text-foreground">
            <DoorOpen className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
            {t('checkIn.noWalkIns')}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * What Find my visit said, short of a match: when check-in opens, that it has closed, to see the
 * counter, or that nothing matched (with "I'm walking in" while the place takes walk-ins).
 */
function FindNoticeBox({
  notice,
  timeZone,
  takesWalkIns,
  onWalkIn,
}: {
  notice: FindNotice;
  timeZone: string;
  takesWalkIns: boolean;
  onWalkIn: () => void;
}) {
  const { t, locale } = useI18n();
  const text =
    notice.kind === 'too_early'
      ? t('checkIn.tooEarly', { time: clockTime(notice.opensAt, timeZone, locale) })
      : notice.kind === 'too_late'
        ? t('checkIn.tooLate')
        : notice.kind === 'ask_counter'
          ? t('checkIn.seeCounter')
          : notice.kind === 'expired'
            ? t('checkIn.expired')
            : takesWalkIns
              ? t('checkIn.notFound')
              : `${t('checkIn.notFound')} ${t('checkIn.seeCounter')}`;
  const calm = notice.kind === 'too_early' || notice.kind === 'expired';
  return (
    <div
      className={cn(
        'space-y-3 rounded-xl border p-4',
        calm ? 'border-primary/25 bg-accent' : 'border-warning/50 bg-warning/15',
      )}
    >
      <p role="status" className="flex items-start gap-3 text-base font-medium text-heading">
        <Info
          className={cn('mt-0.5 h-5 w-5 shrink-0', calm ? 'text-primary-strong' : 'text-warning-foreground')}
          aria-hidden="true"
        />
        {text}
      </p>
      {notice.kind === 'not_found' && takesWalkIns && (
        <Button variant="outline" onClick={onWalkIn}>
          <DoorOpen className="h-4 w-4" aria-hidden="true" />
          {t('checkIn.walkingIn')}
        </Button>
      )}
    </div>
  );
}
